import { Flex } from '../flex';
import { Text } from '../text';
import { styled } from '../theme';
import { mergeSx, type SxPropsWithTheme } from '../utils';

import ReadonlyFieldContent from './readonly-field-content';

const ReadonlyFieldWrapper = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2.5, 3.5),
  borderRadius: theme.shape.rounded(1),
  backgroundColor: theme.palette.grey[100],
  minHeight: 44,
}));

interface BaseReadonlyFieldProps {
  label?: string;
  sx?: SxPropsWithTheme;
}

interface ReadonlyStringFieldProps extends BaseReadonlyFieldProps {
  content: string;
  /**
   * Max number of lines to show.
   * Should be used with `content` that is string
   */
  maxLines?: number;
}

interface ReadonlyComponentProps extends BaseReadonlyFieldProps {
  content: JSX.Element;
}

// NOTE: These utilities are copied from https://stackoverflow.com/questions/65805600/type-union-not-checking-for-excess-properties#answer-65805753
type UnionKeys<T> = T extends T ? keyof T : never;
type StrictUnionHelper<T, TAll> = T extends unknown
  ? T & Partial<Record<Exclude<UnionKeys<TAll>, keyof T>, never>>
  : never;

type StrictUnion<T> = StrictUnionHelper<T, T>;

export type ReadonlyFieldProps = StrictUnion<ReadonlyStringFieldProps | ReadonlyComponentProps>;

export default function ReadonlyField(props: Readonly<ReadonlyFieldProps>) {
  const { label, content, maxLines, sx } = props;

  return (
    <Flex
      sx={mergeSx({ flexDirection: 'column', gap: (theme) => theme.spacing(1), width: '100%' }, sx)}
    >
      {label ? (
        <Text variant='body2Semibold' sx={{ width: '100%' }}>
          {label}
        </Text>
      ) : null}
      <ReadonlyFieldWrapper>
        <ReadonlyFieldContent content={content} maxLines={maxLines} />
      </ReadonlyFieldWrapper>
    </Flex>
  );
}
